// src/features/clinical/pages/ClinicalDashboard.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaUsers, FaCalendarCheck, FaPrescription, FaFlask, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import clinicalApi from '../services/clinicalApi';

const ClinicalDashboard = () => {
  const [stats, setStats] = useState({
    totalPatients: 0,
    todayVisits: 0,
    pendingPrescriptions: 0,
    pendingTests: 0
  });
  const [recentVisits, setRecentVisits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [patients, visits, prescriptions] = await Promise.all([
        clinicalApi.getPatients(),
        clinicalApi.getVisits(),
        clinicalApi.getPrescriptions()
      ]);

      const today = new Date().toDateString();
      const todayVisits = visits.data.filter(v => 
        new Date(v.visitDate).toDateString() === today
      );

      setStats({
        totalPatients: patients.data.length,
        todayVisits: todayVisits.length,
        pendingPrescriptions: prescriptions.data.filter(p => p.status === 'ACTIVE').length,
        pendingTests: 0 // You can add test count here
      });

      setRecentVisits(visits.data.slice(0, 5));
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, trend, trendValue }) => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          {trend && (
            <p className={`text-xs mt-2 flex items-center ${
              trend === 'up' ? 'text-green-600' : 'text-red-600'
            }`}>
              {trend === 'up' ? <FaArrowUp className="mr-1" /> : <FaArrowDown className="mr-1" />}
              {trendValue} from last month
            </p>
          )}
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4">
      {/* <h1 className="text-2xl font-bold text-gray-800 mb-6">Clinical Dashboard</h1> */}

      {/* Stats Grid */}
      {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Patients"
          value={stats.totalPatients}
          icon={FaUsers}
          color="bg-blue-600"
          trend="up"
          trendValue="12%"
        />
        <StatCard
          title="Today's Visits"
          value={stats.todayVisits}
          icon={FaCalendarCheck}
          color="bg-green-600"
          trend="up"
          trendValue="8%"
        />
        <StatCard
          title="Active Prescriptions"
          value={stats.pendingPrescriptions}
          icon={FaPrescription}
          color="bg-purple-600"
          trend="down"
          trendValue="3%"
        />
        <StatCard
          title="Pending Tests"
          value={stats.pendingTests}
          icon={FaFlask}
          color="bg-yellow-600"
        />
      </div> */}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Link
          to="/clinical/workflow"
          className="bg-blue-600 text-white p-4 rounded-lg hover:bg-blue-700 transition-colors text-center"
        >
          <p className="font-medium">New Patient Visit</p>
          <p className="text-sm opacity-90">Start a new consultation</p>
        </Link>
        <Link
          to="/clinical/patients/register"
          className="bg-green-600 text-white p-4 rounded-lg hover:bg-green-700 transition-colors text-center"
        >
          <p className="font-medium">Register Patient</p>
          <p className="text-sm opacity-90">Add new patient to system</p>
        </Link>
        <Link
          to="/clinical/prescriptions/new"
          className="bg-purple-600 text-white p-4 rounded-lg hover:bg-purple-700 transition-colors text-center"
        >
          <p className="font-medium">Create Prescription</p>
          <p className="text-sm opacity-90">For existing patient</p>
        </Link>
      </div>

      {/* Recent Visits */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Recent Visits</h2>
        </div>
        <div className="divide-y divide-gray-200">
          {recentVisits.map((visit) => (
            <div key={visit.id} className="p-4 hover:bg-gray-50">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium text-gray-900">{visit.patientName}</p>
                  <p className="text-sm text-gray-500">Dr. {visit.doctorName} • {visit.department}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">{new Date(visit.visitDate).toLocaleDateString()}</p>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    visit.status === 'COMPLETED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {visit.status}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ClinicalDashboard;